
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  FiChevronDown, FiX, FiUser, FiLock, FiLogOut,
  FiCheckSquare, FiBriefcase, FiMail, FiPhone, FiMapPin,
  FiBell, FiSettings, FiCalendar, FiClock, FiAlertCircle,
  FiPlus, FiUsers, FiArchive, FiTarget, FiMapPin as FiLocation
} from 'react-icons/fi';
import TaskForm from '../pages/TaskForm';
import LeadForm from '../LeadMng/components/LeadForm';
import LeadsService from '../LeadMng/services/leadsService';
import { AUTH_API_BASE_URL as API_BASE_URL, LEADS_API_BASE_URL } from '../LeadMng/config/api';
import { getSocket } from '../services/socket';
import { Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';  // Already imported, now we'll use it

function TopBar({
  currentPage,
  currentUser,
  onLogout,
  getUserInitials,
  onNavigate,
  onToggleSidebar
}) {
  // State management
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [showCompanyProfile, setShowCompanyProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Profile states
  const [companyProfile, setCompanyProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [companyProfileError, setCompanyProfileError] = useState('');

  // Change password states
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');

  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationFilter, setNotificationFilter] = useState('unread');

  // Search state
  const [showSearch, setShowSearch] = useState(false);

  // Task Form States
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskFormData, setTaskFormData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [departments, setDepartments] = useState([]);

  // Lead Form States
  const [showLeadForm, setShowLeadForm] = useState(false);

  // Permission states
  const [accessPermissions, setAccessPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);

  // Advanced features states
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [showActivityFeed, setShowActivityFeed] = useState(false);
  const [showQuickStats, setShowQuickStats] = useState(false);
  const [quickStats, setQuickStats] = useState({
    tasksToday: 0,
    pendingTasks: 0,
    completedTasks: 0,
    activeEmployees: 0,
    totalLeads: 0,
    todayAttendance: 0,
    avgPerformance: 0
  });

  const [showSettings, setShowSettings] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    email: true,
    inApp: true,
    push: false,
    taskAssignments: true,
    deadlines: true,
    mentions: true,
    shiftRequests: true
  });

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000);


  // Add new states for company update
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [companyFormData, setCompanyFormData] = useState({});
  const [updateCompanyLoading, setUpdateCompanyLoading] = useState(false);
  const [updateCompanyError, setUpdateCompanyError] = useState('');
  const [updateCompanySuccess, setUpdateCompanySuccess] = useState('');



  // Employee Performance Tracking
  const [showPerformancePanel, setShowPerformancePanel] = useState(false);

  const navigate = useNavigate();



  // Helper function to check permissions
  const hasPermission = (permission) => {
    if (userRole === 'company') {
      return true;
    }
    return accessPermissions.includes(permission);
  };


  // Decode token and set permissions on mount
  useEffect(() => {
    try {
      const token = localStorage.getItem('authToken');

      if (!token) {
        return;
      }

      const cleanToken = token.replace(/['"]/g, '').trim();
      const decoded = jwtDecode(cleanToken);

      setAccessPermissions(decoded.accessPermissions || []);
      setUserRole(decoded.role);
    } catch (error) {
      console.error('Failed to decode token:', error);
    }
  }, []);


  // Load preferences from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    const savedNotificationPrefs = localStorage.getItem('notificationPreferences');
    const savedAutoRefresh = localStorage.getItem('autoRefresh') !== 'false';

    setDarkMode(savedDarkMode);
    setAutoRefresh(savedAutoRefresh);
    if (savedNotificationPrefs) {
      setNotificationPreferences(JSON.parse(savedNotificationPrefs));
    }

    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Socket: increment bell count on server notifications
  useEffect(() => {
    try {
      const socket = getSocket();
      const onNew = (payload) => {
        setUnreadCount((c) => c + 1);
        setNotifications((prev) => [
          {
            id: `srv-${payload.id}`,
            type: payload.type,
            title: payload.title || (payload.type === 'task' ? 'Task Assigned' : payload.type === 'ticket' ? 'Ticket Assigned' : payload.type === 'project' ? 'Added to Project' : payload.type === 'milestone' ? 'Milestone Assigned' : 'Lead Assigned'),
            message: payload.message || '',
            time: 'just now',
            unread: true,
            priority: (payload.meta && payload.meta.priority) || 'medium'
          },
          ...prev
        ].slice(0, 20));
      };
      socket.on('notification:new', onNew);
      return () => {
        socket.off('notification:new', onNew);
      };
    } catch (e) {
      // silent
    }
  }, []);

  // Poll unread count as a fallback to ensure badge accuracy
  const prevUnreadCountRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
        if (!token) return;
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch actual notification lists to apply the same de-duplication logic
        const [globalRes, leadRes] = await Promise.all([
          fetch(`${API_BASE_URL}/notifications`, { headers }).catch(() => null),
          fetch(`${LEADS_API_BASE_URL}/lead-notifications/all`, { headers }).catch(() => null)
        ]);

        let globalNotifications = [];
        if (!cancelled && globalRes && globalRes.ok) {
          const globalRaw = await globalRes.json();
          globalNotifications = (Array.isArray(globalRaw) ? globalRaw : []).map(n => ({
            message: n.message,
            unread: n.unread
          }));
        }

        let leadNotifications = [];
        if (!cancelled && leadRes && leadRes.ok) {
          const leadData = await leadRes.json();
          const dataArray = Array.isArray(leadData) ? leadData : (Array.isArray(leadData.data) ? leadData.data : (Array.isArray(leadData.notifications) ? leadData.notifications : []));
          leadNotifications = dataArray.map(n => ({
            message: n.message || `New interaction on lead ${n.leadName || ''}`,
            unread: n.isRead === false || n.read === false || !n.isRead
          }));
        }

        if (!cancelled) {
          const allNotifications = [...globalNotifications, ...leadNotifications];
          const uniqueNotifications = [];
          const seen = new Set();
          for (const n of allNotifications) {
            const msg = (n.message || '').trim().toLowerCase();
            if (!seen.has(msg)) {
              seen.add(msg);
              uniqueNotifications.push(n);
            }
          }

          const totalUnread = uniqueNotifications.filter(n => n.unread).length;

          if (totalUnread > prevUnreadCountRef.current && prevUnreadCountRef.current > 0) {
            if (typeof window.toast?.info === 'function') {
              window.toast.info('You have new notifications!', { icon: '🔔' });
            }
          }
          prevUnreadCountRef.current = totalUnread;
          setUnreadCount(totalUnread);
        }
      } catch {
        // silent
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 20000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Get company ID helper
  const getCompanyId = useCallback(async () => {
    // Try localStorage first
    const storedCompanyId = localStorage.getItem('companyId') || localStorage.getItem('selectedCompanyId');
    if (storedCompanyId) return storedCompanyId;

    const token = localStorage.getItem('authToken');
    if (!token) return null;

    try {
      const cleanToken = token.replace(/['"]/g, '').trim();
      const decodedToken = jwtDecode(cleanToken);

      const decodedCompanyId = decodedToken.companyId || decodedToken.company?._id || decodedToken.company || decodedToken.id;
      if (decodedCompanyId) {
        return typeof decodedCompanyId === 'object' ? (decodedCompanyId._id || decodedCompanyId.id) : decodedCompanyId;
      }
      return null;
    } catch (error) {
      console.error('JWT decoding failed:', error);
      return null;
    }
  }, []);


  // Get current employee ID
  const getEmployeeId = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return null;

    try {
      const cleanToken = token.replace(/['"]/g, '').trim();
      const decodedToken = jwtDecode(cleanToken);
      return decodedToken.id;
    } catch (error) {
      console.error('JWT decoding failed:', error);
      return null;
    }
  }, []);


  // Fetch departments for task form
  const fetchDepartments = useCallback(async () => {
    try {
      const companyId = await getCompanyId();
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();

      if (!companyId || !token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/departments/company/${companyId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const deptList = Array.isArray(data) ? data : (data.data || data.departments || []);
        setDepartments(deptList);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, [getCompanyId]);


  // Load departments on mount
  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);


  // Fetch Quick Stats with Real API Data
  const fetchQuickStats = useCallback(async () => {
    try {
      const companyId = await getCompanyId();
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();

      if (!companyId || !token) return;

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Fetch multiple endpoints in parallel
      const [tasksRes, employeesRes, leadsRes, attendanceRes] = await Promise.all([
        fetch(`${API_BASE_URL}/task/tasks`, { headers }),
        fetch(`${API_BASE_URL}/employees/company/${companyId}`, { headers }),
        fetch(`${API_BASE_URL}/lead/`, { headers }),
        fetch(`${API_BASE_URL}/attendance/`, { headers })
      ]);

      const tasks = tasksRes.ok ? await tasksRes.json() : [];
      const employees = employeesRes.ok ? await employeesRes.json() : [];
      const leads = leadsRes.ok ? await leadsRes.json() : [];
      const attendance = attendanceRes.ok ? await attendanceRes.json() : [];

      // Calculate today's tasks
      const today = new Date().toDateString();
      const todayTasks = Array.isArray(tasks) ? tasks.filter(task => {
        const taskDate = new Date(task.startDateTime).toDateString();
        return taskDate === today;
      }) : [];

      // Calculate today's attendance
      const todayAttendance = Array.isArray(attendance) ? attendance.filter(att => {
        const attDate = new Date(att.date).toDateString();
        return attDate === today;
      }).length : 0;

      setQuickStats({
        tasksToday: todayTasks.length,
        pendingTasks: Array.isArray(tasks) ? tasks.filter(t => t.status === 'pending').length : 0,
        completedTasks: Array.isArray(tasks) ? tasks.filter(t => t.status === 'completed').length : 0,
        activeEmployees: Array.isArray(employees) ? employees.length : 0,
        totalLeads: Array.isArray(leads) ? leads.length : 0,
        todayAttendance: todayAttendance,
        avgPerformance: 0 // Will be calculated from credit points
      });

    } catch (error) {
      console.error('Error fetching quick stats:', error);
    }
  }, [getCompanyId]);




  // Helper function to get time ago
  const getTimeAgo = (date) => {
    if (!date) return 'just now';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    fetchQuickStats();


    const interval = setInterval(() => {
      fetchQuickStats();

    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchQuickStats]);

  // Fetch real notifications from backend (global + lead notifications)
  const fetchNotifications = useCallback(async () => {
    try {
      setNotificationLoading(true);
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();

      if (!token) return;

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const companyId = await getCompanyId();

      // Fetch global notifications + lead notifications + employees in parallel
      const [globalRes, leadRes, employeesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/notifications`, { headers }).catch(() => null),
        fetch(`${LEADS_API_BASE_URL}/lead-notifications/all`, { headers }).catch(() => null),
        companyId ? fetch(`${API_BASE_URL}/employees/company/${companyId}`, { headers }).catch(() => null) : null
      ]);

      // Parse employees list
      let employees = [];
      if (employeesRes && employeesRes.ok) {
        const empData = await employeesRes.json();
        if (Array.isArray(empData)) {
          employees = empData;
        } else if (empData && empData.success && Array.isArray(empData.data)) {
          employees = empData.data;
        } else if (empData && Array.isArray(empData.employees)) {
          employees = empData.employees;
        }
      } else if (companyId) {
        // Fallback to /employees generally
        const fallbackRes = await fetch(`${API_BASE_URL}/employees`, { headers }).catch(() => null);
        if (fallbackRes && fallbackRes.ok) {
          const empData = await fallbackRes.json();
          if (Array.isArray(empData)) {
            employees = empData;
          } else if (empData && empData.success && Array.isArray(empData.data)) {
            employees = empData.data;
          } else if (empData && Array.isArray(empData.employees)) {
            employees = empData.employees;
          }
        }
      }

      // Fetch company profile to resolve companyId to company name
      let currentCompanyName = '';
      if (companyId) {
        try {
          const compRes = await fetch(`${API_BASE_URL}/companies/${companyId}`, { headers }).catch(() => null);
          if (compRes && compRes.ok) {
            const compData = await compRes.json();
            currentCompanyName = compData.businessName || compData.name || '';
          }
        } catch (e) {
          console.error("Failed to fetch company name:", e);
        }
      }

      // Retrieve logged in user info
      let loggedInUser = null;
      try {
        const userInfoStr = localStorage.getItem('userInfo');
        if (userInfoStr) {
          loggedInUser = JSON.parse(userInfoStr);
        }
      } catch (e) {
        console.error("Error reading userInfo for name resolution:", e);
      }

      // Helper function to resolve 24-character hexadecimal ObjectId to Name
      const getEmployeeName = (idOrString) => {
        if (!idOrString) return '';
        const idStr = String(idOrString).trim();
        if (/^[0-9a-fA-F]{24}$/.test(idStr)) {
          // Check loggedInUser first
          if (loggedInUser && String(loggedInUser.id || loggedInUser._id) === idStr) {
            return loggedInUser.teamMemberName || loggedInUser.name || loggedInUser.businessName || 'Me';
          }
          // Check currentUser prop
          if (currentUser && String(currentUser.id || currentUser._id) === idStr) {
            return currentUser.teamMemberName || currentUser.name || currentUser.businessName || 'Me';
          }
          const emp = employees.find(e => String(e._id || e.id) === idStr);
          if (emp) {
            return emp.name;
          }
          if (idStr === companyId && currentCompanyName) {
            return currentCompanyName;
          }
          return 'Pro One Click Automation Pvt Ltd';
        }
        return idOrString;
      };

      // Helper function to resolve ObjectIds in any text message
      const resolveNamesInText = (text) => {
        if (!text || typeof text !== 'string') return text;
        const objectIdRegex = /[0-9a-fA-F]{24}/g;
        return text.replace(objectIdRegex, (match) => {
          // Check loggedInUser
          if (loggedInUser && String(loggedInUser.id || loggedInUser._id) === match) {
            return loggedInUser.teamMemberName || loggedInUser.name || loggedInUser.businessName || 'Me';
          }
          // Check currentUser prop
          if (currentUser && String(currentUser.id || currentUser._id) === match) {
            return currentUser.teamMemberName || currentUser.name || currentUser.businessName || 'Me';
          }
          const emp = employees.find(e => String(e._id || e.id) === match);
          if (emp) return emp.name;
          if (match === companyId && currentCompanyName) return currentCompanyName;
          return 'Pro One Click Automation Pvt Ltd';
        });
      };

      // Parse global notifications
      const globalRaw = (globalRes && globalRes.ok) ? await globalRes.json() : [];
      const globalNotifications = (Array.isArray(globalRaw) ? globalRaw : []).map(n => {
        const rawCompanyName = (n.meta && n.meta.companyName) || '';
        const resolvedCompanyName = getEmployeeName(rawCompanyName);
        const resolvedMessage = resolveNamesInText(n.message);
        return {
          id: n.id,
          type: n.type,
          source: 'global',
          title: n.title || (n.type === 'task' ? 'Task Assigned' : n.type === 'ticket' ? 'Ticket Assigned' : n.type === 'project' ? 'Added to Project' : n.type === 'milestone' ? 'Milestone Assigned' : 'Notification'),
          message: resolvedMessage,
          time: getTimeAgo(n.createdAt),
          unread: n.unread,
          priority: (n.meta && n.meta.priority) || 'medium',
          companyName: resolvedCompanyName,
          createdAt: n.createdAt,
          meta: n.meta || null,
          taskId: n.taskId || (n.meta && n.meta.taskId) || null
        };
      });

      // Parse lead notifications
      let leadNotifications = [];
      if (leadRes && leadRes.ok) {
        const leadData = await leadRes.json();
        const dataArray = Array.isArray(leadData) ? leadData : (Array.isArray(leadData.data) ? leadData.data : (Array.isArray(leadData.notifications) ? leadData.notifications : []));

        if (dataArray.length > 0) {
          leadNotifications = dataArray.map(n => {
            const rawCompanyName = n.leadCompany || '';
            const resolvedCompanyName = getEmployeeName(rawCompanyName);
            const resolvedMessage = resolveNamesInText(n.message || `New interaction on lead ${n.leadName || ''}`);
            return {
              id: n.id || n._id,
              type: n.type || 'lead_assigned',
              source: 'lead',
              category: n.category || 'lead',
              title: n.type === 'lead_assigned' ? '👤 Lead Assigned'
                : n.type === 'follow_up_overdue' ? '🔴 Follow-up Overdue'
                  : n.type === 'follow_up_today' ? '📞 Follow-up Today'
                    : n.type === 'follow_up_upcoming' ? '📅 Upcoming Follow-up'
                      : (n.title || 'Lead Notification'),
              message: resolvedMessage,
              time: getTimeAgo(n.scheduledDate || n.createdAt),
              unread: n.isRead === false || n.read === false || !n.isRead,
              priority: n.priority || 'medium',
              companyName: resolvedCompanyName,
              createdAt: n.createdAt || new Date().toISOString(),
              leadName: n.leadName,
              leadPhone: n.leadPhone,
              scheduledDate: n.scheduledDate
            };
          });
        }
      }

      // Merge and sort: unread first, then by most recent
      const allNotifications = [...globalNotifications, ...leadNotifications];

      // De-duplicate by normalized message to prevent double-rendering if returned by both APIs
      const uniqueNotifications = [];
      const seen = new Set();
      for (const n of allNotifications) {
        const msg = (n.message || '').trim().toLowerCase();
        if (!seen.has(msg)) {
          seen.add(msg);
          uniqueNotifications.push(n);
        }
      }

      const sortedNotifications = uniqueNotifications.sort((a, b) => {
        if (b.unread !== a.unread) return (b.unread ? 1 : 0) - (a.unread ? 1 : 0);
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setNotifications(sortedNotifications);
      setUnreadCount(sortedNotifications.filter(n => n.unread).length);

    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setNotificationLoading(false);
    }
  }, []);

  // Hook up lead events to force a notification fetch
  useEffect(() => {
    const handleLeadEvent = () => fetchNotifications();
    window.addEventListener('leadDataUpdated', handleLeadEvent);
    window.addEventListener('leadCreated', handleLeadEvent);
    window.addEventListener('lead-assigned', handleLeadEvent);
    return () => {
      window.removeEventListener('leadDataUpdated', handleLeadEvent);
      window.removeEventListener('leadCreated', handleLeadEvent);
      window.removeEventListener('lead-assigned', handleLeadEvent);
    };
  }, [fetchNotifications]);

  // Mark notification as read
  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      const notification = notifications.find(n => n.id === notificationId);
      if (token) {
        if (notification?.source === 'lead') {
          await fetch(`${LEADS_API_BASE_URL}/lead-notifications/${notificationId}/read`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } else {
          await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        }
      }
    } catch { }
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, unread: false, justRead: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Handle clicking a notification
  const handleNotificationClick = async (notification) => {
    // 1. Mark notification as read
    await markNotificationAsRead(notification.id);

    // 2. Check if notification is task-related
    const isTask = notification.type === 'task' ||
      notification.type?.toLowerCase().includes('task') ||
      notification.title?.toLowerCase().includes('task') ||
      notification.message?.toLowerCase().includes('task');

    const isLead = notification.source === 'lead' ||
      notification.type === 'lead' ||
      notification.type?.toLowerCase().includes('lead') ||
      notification.title?.toLowerCase().includes('lead') ||
      notification.message?.toLowerCase().includes('lead');

    if (isTask) {
      // Close dropdown
      setIsNotificationDropdownOpen(false);

      // Save notification details to localStorage so Tasks.jsx can open detailed modal on mount/load
      localStorage.setItem('clickedTaskNotification', JSON.stringify(notification));

      // Switch active tab in ComprehensiveDashboard if the user is already there
      localStorage.setItem('activeDashboardTab', 'tasks');
      window.dispatchEvent(new CustomEvent('switchDashboardTab', { detail: { tab: 'tasks' } }));

      // Navigate to tasks section
      if (onNavigate) {
        onNavigate('/tasks', 'Tasks');
      } else {
        navigate('/tasks');
      }
    } else if (isLead) {
      // Close dropdown
      setIsNotificationDropdownOpen(false);

      // Switch active tab in ComprehensiveDashboard if the user is already there
      localStorage.setItem('activeDashboardTab', 'leads');
      window.dispatchEvent(new CustomEvent('switchDashboardTab', { detail: { tab: 'leads' } }));

      // Navigate directly to lead profile if ID is available
      const leadId = notification.meta?.leadId || notification.leadId;
      if (leadId) {
        if (onNavigate) {
          onNavigate(`/lead/${leadId}`, 'Lead Profile');
        } else {
          navigate(`/lead/${leadId}`);
        }
      } else {
        if (onNavigate) {
          onNavigate('/lead-mng', 'Lead Management');
        } else {
          navigate('/lead-mng');
        }
      }
    }
  };


  // Mark all as read (global + lead)
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (token) {
        // Mark global notifications as read
        await fetch(`${API_BASE_URL}/notifications/mark-all-read`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => { });
        // Mark lead notifications as read
        await fetch(`${LEADS_API_BASE_URL}/lead-notifications/mark-all-read`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => { });
      }
    } catch { }
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
    setUnreadCount(0);
  };


  // Delete notification
  const deleteNotification = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (token) {
        if (notification?.source === 'lead') {
          await fetch(`${LEADS_API_BASE_URL}/lead-notifications/${notificationId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } else {
          await fetch(`${API_BASE_URL}/notifications/${notificationId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
      }
    } catch { }
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (notification?.unread) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };


  // Get filtered notifications
  const getFilteredNotifications = () => {
    let filtered = notifications;

    if (notificationFilter === 'unread') {
      filtered = filtered.filter(n => n.unread || n.justRead);
    }

    return filtered;
  };

  // Clear justRead flags when dropdown closes
  useEffect(() => {
    if (!isNotificationDropdownOpen) {
      setNotifications(prev => prev.map(n => n.justRead ? { ...n, justRead: false } : n));
    }
  }, [isNotificationDropdownOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.profile-dropdown') &&
        !target.closest('.notification-dropdown') &&
        !target.closest('.activity-feed') &&
        !target.closest('.performance-panel') &&
        !target.closest('.settings-panel')) {
        setIsProfileDropdownOpen(false);
        setIsNotificationDropdownOpen(false);
        setShowActivityFeed(false);
        setShowSettings(false);
        setShowPerformancePanel(false);
      }

      if (showCommandPalette && !target.closest('.command-palette')) {
        setShowCommandPalette(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCommandPalette]);


  // Fetch company profile
  const fetchCompanyProfile = async () => {
    setShowCompanyProfile(true);
    setCompanyProfile(null);
    setCompanyProfileError('');
    setProfileLoading(true);
    setCompanyFormData({});
    setUpdateCompanyError('');
    setUpdateCompanySuccess('');
    setIsEditingCompany(false);


    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      if (!token) throw new Error('No authentication token found');

      const decoded = jwtDecode(token);

      if (decoded?.role !== 'company') {
        throw new Error('View Profile is only available for company users');
      }

      const id = decoded.id || currentUser?.id;
      if (!id) throw new Error('No valid company ID found');

      const response = await fetch(`${API_BASE_URL}/companies/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to fetch profile (HTTP ${response.status})`);
      }

      const data = await response.json();
      const sanitizedData = { ...data };
      delete sanitizedData.password;

      setCompanyProfile(sanitizedData);
      setCompanyFormData(sanitizedData);  // Initialize form data

    } catch (error) {
      setCompanyProfileError(error.message || 'Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    setUpdateCompanyError('');
    setUpdateCompanySuccess('');

    // Basic validation
    if (!companyFormData.businessName?.trim()) {
      setUpdateCompanyError('Business name is required');
      return;
    }
    if (!companyFormData.businessEmail?.trim()) {
      setUpdateCompanyError('Business email is required');
      return;
    }

    setUpdateCompanyLoading(true);

    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();
      const decoded = jwtDecode(token);
      const id = decoded.id || currentUser?.id;

      // Prepare FormData for file upload and allowed fields only
      const formData = new FormData();
      formData.append('businessName', companyFormData.businessName || '');
      formData.append('businessEmail', companyFormData.businessEmail || '');
      formData.append('businessPhone', companyFormData.businessPhone || '');
      formData.append('address', companyFormData.address || '');
      formData.append('EmergencyMobNo', companyFormData.EmergencyMobNo || '');
      if (companyFormData.weeklyHoliday) {
        // Send as comma-separated string; backend can split it
        formData.append('weeklyHoliday', Array.isArray(companyFormData.weeklyHoliday)
          ? companyFormData.weeklyHoliday.join(',')
          : companyFormData.weeklyHoliday);
      }
      if (companyFormData.businessLogoFile) {
        formData.append('businessLogo', companyFormData.businessLogoFile);
      }

      const response = await fetch(`${API_BASE_URL}/companies/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Do NOT set 'Content-Type' for FormData; let the browser set it automatically
        },
        body: formData  // Use FormData, not JSON.stringify
      });

      const data = await response.json().catch(() => ({}));
      console.log("data--", data);

      if (response.ok) {
        setUpdateCompanySuccess('Company information updated successfully!');
        setCompanyProfile(data.company);  // Update displayed data with the full response
        setTimeout(() => {
          setIsEditingCompany(false);
          setUpdateCompanySuccess('');
        }, 2000);
      } else {
        setUpdateCompanyError(data.message || 'Failed to update company information');
      }
    } catch (error) {
      console.error('Update company error:', error);
      setUpdateCompanyError('Network error. Please try again.');
    } finally {
      setUpdateCompanyLoading(false);
    }
  };



  const handleCompanyFormChange = (field, value) => {
    if (field === 'businessLogoFile') {
      setCompanyFormData(prev => ({ ...prev, businessLogoFile: value }));
    } else if (field === 'weeklyHoliday') {
      // value should be the updated array from checkbox changes
      setCompanyFormData(prev => ({ ...prev, weeklyHoliday: value }));
    } else {
      setCompanyFormData(prev => ({ ...prev, [field]: value }));
    }
    if (updateCompanyError) setUpdateCompanyError('');
  };
  // Handle change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError('');
    setChangePasswordSuccess('');

    if (!changePasswordForm.currentPassword || !changePasswordForm.newPassword || !changePasswordForm.confirmPassword) {
      setChangePasswordError('All fields are required');
      return;
    }

    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      setChangePasswordError('New passwords do not match');
      return;
    }

    if (changePasswordForm.newPassword.length < 6) {
      setChangePasswordError('New password must be at least 6 characters long');
      return;
    }

    setChangePasswordLoading(true);

    try {
      const token = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();

      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: changePasswordForm.currentPassword,
          newPassword: changePasswordForm.newPassword
        })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setChangePasswordSuccess(data.message || 'Password changed successfully!');
        setTimeout(() => {
          setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setShowChangePassword(false);
          setChangePasswordSuccess('');
        }, 2000);
      } else {
        setChangePasswordError(data.message || 'Failed to change password');
      }
    } catch (error) {
      setChangePasswordError('Network error. Please try again.');
    } finally {
      setChangePasswordLoading(false);
    }
  };


  const handleChangePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setChangePasswordForm(prev => ({ ...prev, [name]: value }));
    if (changePasswordError) setChangePasswordError('');
  };


  // Toggle functions
  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
    setIsNotificationDropdownOpen(false);
    setShowSearch(false);
    setShowActivityFeed(false);
    setShowPerformancePanel(false);
  };

  const toggleNotificationDropdown = () => {
    setIsNotificationDropdownOpen(!isNotificationDropdownOpen);
    setIsProfileDropdownOpen(false);
    setShowSearch(false);
    setShowActivityFeed(false);
    setShowPerformancePanel(false);
    if (!isNotificationDropdownOpen) {
      fetchNotifications();
      setNotificationFilter('unread');
    }
  };


  // Handle Create Task Click
  const handleCreateTaskClick = () => {
    setIsEditMode(false);
    setTaskFormData(null);
    setShowTaskForm(true);
    setShowCommandPalette(false);
  };

  const handleCreateLeadClick = () => {
    setShowLeadForm(true);
    setShowCommandPalette(false);
  };

  const handleAddLeadSubmit = async (leadData) => {
    try {
      const result = await LeadsService.createLead(leadData);
      if (result.success) {
        setShowLeadForm(false);
        if (window.toast) {
          window.toast.success('Lead created successfully!');
        } else {
          alert('Lead created successfully!');
        }
        // Force refresh across components
        window.dispatchEvent(new CustomEvent('leadDataUpdated'));
        window.dispatchEvent(new CustomEvent('leadCreated'));
        fetchQuickStats();
      } else {
        if (window.toast) {
          window.toast.error(`Failed to create lead: ${result.error}`);
        } else {
          alert(`Failed to create lead: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('Error creating lead:', error);
      if (window.toast) window.toast.error('Failed to create lead');
    }
  };

  // Handle Task Form Submit
  const handleTaskFormSubmit = async (taskData) => {
    try {
      setShowTaskForm(false);
      setTaskFormData(null);
      setIsEditMode(false);

      window.dispatchEvent(new CustomEvent('taskCreated'));

      if (window.toast) {
        window.toast.success('Task created successfully!');
      }


      fetchQuickStats();
    } catch (error) {
      console.error('Error creating task:', error);
      if (window.toast) {
        window.toast.error('Failed to create task');
      }
    }
  };


  // Handle Task Form Close
  const handleTaskFormClose = () => {
    setShowTaskForm(false);
    setTaskFormData(null);
    setIsEditMode(false);
  };


  // Render user profile
  const renderUserProfile = () => {
    if (!currentUser) {
      return (
        <div className="flex items-center space-x-3 animate-pulse">
          <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
          <div className="hidden md:block space-y-1">
            <div className="h-4 w-20 bg-gray-300 rounded"></div>
            <div className="h-3 w-16 bg-gray-300 rounded"></div>
          </div>
        </div>
      );
    }

    const getDisplayName = (user) =>
      (user?.teamMemberName || user?.name || user?.businessName || user?.userName || 'User');

    return (
      <div className="flex items-center space-x-2">
        <div className="w-8 h-8 bg-gradient-to-r from-[#7367F0] to-[#5A67D8] rounded-full flex items-center justify-center shadow-sm">
          {currentUser.avatar || currentUser.profileImage || currentUser.businessLogo ? (
            <img
              src={currentUser.avatar || currentUser.profileImage || currentUser.businessLogo}
              alt={getDisplayName(currentUser)}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className="text-white font-medium text-xs">
              {getUserInitials(getDisplayName(currentUser))}
            </span>
          )}
        </div>
        <div className="hidden md:block text-left">
          <p className="text-[13px] font-semibold text-slate-100 dark:text-white leading-tight">
            {getDisplayName(currentUser)}
          </p>
          <p className="text-[11px] text-slate-400 dark:text-slate-400 leading-tight">
            {currentUser?.email || currentUser?.role || ''}
          </p>
        </div>
        <FiChevronDown className={`text-slate-400 w-3 h-3 ml-1 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
      </div>
    );
  };

  // Render Quick Stats Panel
  const renderQuickStats = () => {
    if (!showQuickStats) return null;

    return (
      <div className="flex items-center space-x-4 px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-xl">
        <div className="flex items-center space-x-2">
          <FiCheckSquare className="text-green-500" />
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400">Tasks Today</p>
            <p className="text-sm font-bold text-[#2C2C2C] dark:text-white">{quickStats.tasksToday}</p>
          </div>
        </div>
        <div className="w-px h-8 bg-gray-300 dark:bg-gray-600"></div>
        <div className="flex items-center space-x-2">
          <FiClock className="text-yellow-500" />
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400">Pending</p>
            <p className="text-sm font-bold text-[#2C2C2C] dark:text-white">{quickStats.pendingTasks}</p>
          </div>
        </div>
        <div className="w-px h-8 bg-gray-300 dark:bg-gray-600"></div>
        <div className="flex items-center space-x-2">
          <FiUsers className="text-purple-500" />
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400">Employees</p>
            <p className="text-sm font-bold text-[#2C2C2C] dark:text-white">{quickStats.activeEmployees}</p>
          </div>
        </div>
        <div className="w-px h-8 bg-gray-300 dark:bg-gray-600"></div>
        <div className="flex items-center space-x-2">
          <FiTarget className="text-blue-500" />
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400">Leads</p>
            <p className="text-sm font-bold text-[#2C2C2C] dark:text-white">{quickStats.totalLeads}</p>
          </div>
        </div>
      </div>
    );
  };


  // Render company profile modal (keeping existing implementation)
  const renderCompanyProfileModal = () => {
    if (!showCompanyProfile) return null;

    const formatDate = (dateString) => {
      if (!dateString) return 'Not specified';
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch {
        return 'Invalid date';
      }
    };

    const renderHolidayChips = (holidays) => {
      if (!Array.isArray(holidays) || holidays.length === 0)
        return <span className="text-gray-500 dark:text-gray-400">Not specified</span>;
      return (
        <div className="flex flex-wrap gap-2 mt-1">
          {holidays.map((day, index) => (
            <span key={index} className="px-3 py-1 bg-[#7367F0] bg-opacity-10 text-[#7367F0] rounded-full text-xs font-medium">
              {day}
            </span>
          ))}
        </div>
      );
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4 animate-fadeIn">
        <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slideUp">
          <div className="sticky top-0 bg-white dark:bg-gray-800 z-10 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-[#2C2C2C] dark:text-white flex items-center">
              <FiBriefcase className="mr-3 text-[#7367F0]" />
              {isEditingCompany ? 'Update Company Profile' : 'Company Profile'}
            </h2>
            <div className="flex items-center space-x-2">
              {!isEditingCompany && currentUser?.role === 'company' && (
                <button
                  onClick={() => setIsEditingCompany(true)}
                  className="px-4 py-2 bg-[#7367F0] text-white rounded-lg hover:bg-[#5A67D8] transition-colors text-sm font-medium"
                >
                  Edit
                </button>
              )}
              <button
                onClick={() => {
                  setShowCompanyProfile(false);
                  setIsEditingCompany(false);
                  setCompanyFormData({});
                  setUpdateCompanyError('');
                  setUpdateCompanySuccess('');
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                disabled={profileLoading || updateCompanyLoading}
              >
                <FiX className="text-gray-500 dark:text-gray-400 text-xl" />
              </button>
            </div>
          </div>

          {profileLoading && (
            <div className="p-12 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#7367F0] border-t-transparent"></div>
              <span className="mt-4 text-[#6E6E6E] dark:text-gray-400">Loading profile...</span>
            </div>
          )}

          {companyProfileError && !profileLoading && (
            <div className="p-6">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <p className="text-red-600 dark:text-red-400 font-medium">{companyProfileError}</p>
                <button
                  onClick={() => setShowCompanyProfile(false)}
                  className="mt-3 text-red-600 dark:text-red-400 underline text-sm hover:text-red-700 dark:hover:text-red-300"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {!profileLoading && companyProfile && (
            <form onSubmit={handleUpdateCompany} className="p-6 space-y-6">
              {updateCompanyError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <p className="text-red-600 dark:text-red-400 font-medium">{updateCompanyError}</p>
                </div>
              )}
              {updateCompanySuccess && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                  <p className="text-green-600 dark:text-green-400 font-medium">{updateCompanySuccess}</p>
                </div>
              )}


              <div className="bg-gradient-to-r from-[#7367F0]/10 to-[#5A67D8]/10 rounded-xl p-6">

                <h3 className="text-lg font-semibold text-[#2C2C2C] dark:text-white mb-4 flex items-center">
                  <FiUser className="mr-2 text-[#7367F0]" />
                  Basic Information
                </h3>
                <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
                  {companyProfile.businessLogo ? (
                    <img
                      src={companyProfile.businessLogo}
                      alt="Business Logo"
                      className="w-24 h-24 rounded-xl object-cover shadow-lg border-2 border-white dark:border-gray-700"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-24 h-24 bg-gradient-to-r from-[#7367F0] to-[#5A67D8] rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-3xl">
                        {(companyFormData.businessName || 'C').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[#6E6E6E] dark:text-gray-400 mb-1">Business Name</label>
                    {isEditingCompany ? (
                      <input
                        type="text"
                        value={companyFormData.businessName || ''}
                        onChange={(e) => handleCompanyFormChange('businessName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#7367F0] focus:border-transparent bg-white dark:bg-gray-700 text-[#2C2C2C] dark:text-white"
                        required
                      />
                    ) : (
                      <p className="text-2xl font-bold text-[#2C2C2C] dark:text-white">
                        {companyProfile.businessName || 'Not specified'}
                      </p>
                    )}
                  </div>
                </div>
                {isEditingCompany && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-[#6E6E6E] dark:text-gray-400 mb-1">Business Logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleCompanyFormChange('businessLogoFile', e.target.files[0])}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#7367F0] focus:border-transparent bg-white dark:bg-gray-700 text-[#2C2C2C] dark:text-white"
                    />
                  </div>
                )}
              </div>

              <div>
                <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <h4 className="text-lg font-semibold text-[#2C2C2C] dark:text-white mb-4 flex items-center">
                    <FiMail className="mr-2 text-[#7367F0]" />
                    Contact Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4">  {/* 2x2 grid for the 4 fields */}
                    <div className="flex items-start space-x-3">
                      <FiMapPin className="text-[#7367F0] mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-[#6E6E6E] dark:text-gray-400 mb-1">Address</label>
                        {isEditingCompany ? (
                          <input
                            type="text"
                            value={companyFormData.address || ''}
                            onChange={(e) => handleCompanyFormChange('address', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#7367F0] focus:border-transparent bg-white dark:bg-gray-700 text-[#2C2C2C] dark:text-white text-sm"
                          />
                        ) : (
                          <p className="text-sm text-[#2C2C2C] dark:text-white font-medium">{companyProfile.address || ''}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <FiMail className="text-[#7367F0] mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-[#6E6E6E] dark:text-gray-400 mb-1">Business Email</label>
                        {isEditingCompany ? (
                          <input
                            type="email"
                            value={companyFormData.businessEmail || ''}
                            onChange={(e) => handleCompanyFormChange('businessEmail', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#7367F0] focus:border-transparent bg-white dark:bg-gray-700 text-[#2C2C2C] dark:text-white text-sm"
                            required
                          />
                        ) : (
                          <p className="text-sm text-[#2C2C2C] dark:text-white font-medium">{companyProfile.businessEmail || 'Not specified'}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <FiPhone className="text-[#7367F0] mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-[#6E6E6E] dark:text-gray-400 mb-1">Business Phone</label>
                        {isEditingCompany ? (
                          <input
                            type="tel"
                            value={companyFormData.businessPhone || ''}
                            onChange={(e) => handleCompanyFormChange('businessPhone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#7367F0] focus:border-transparent bg-white dark:bg-gray-700 text-[#2C2C2C] dark:text-white text-sm"
                          />
                        ) : (
                          <p className="text-sm text-[#2C2C2C] dark:text-white font-medium">{companyProfile.businessPhone || 'Not specified'}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <FiPhone className="text-[#FF4560] mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-[#6E6E6E] dark:text-gray-400 mb-1">Emergency Contact</label>
                        {isEditingCompany ? (
                          <input
                            type="tel"
                            value={companyFormData.EmergencyMobNo || ''}
                            onChange={(e) => handleCompanyFormChange('EmergencyMobNo', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#7367F0] focus:border-transparent bg-white dark:bg-gray-700 text-[#2C2C2C] dark:text-white text-sm"
                          />
                        ) : (
                          <p className="text-sm text-[#2C2C2C] dark:text-white font-medium">{companyProfile.EmergencyMobNo || 'Not specified'}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>




              <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-5">
                <h4 className="text-lg font-semibold text-[#2C2C2C] dark:text-white mb-4 flex items-center">
                  <FiBriefcase className="mr-2 text-[#7367F0]" />
                  Business Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-600 rounded-lg">
                    <FiCalendar className="text-[#7367F0]" />
                    <div>
                      <label className="block text-xs font-medium text-[#6E6E6E] dark:text-gray-400 mb-1">Created Date</label>
                      <p className="text-sm text-[#2C2C2C] dark:text-white font-medium">{formatDate(companyProfile.businessCreatedDate)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-600 rounded-lg">
                    <FiSettings className="text-[#7367F0]" />
                    <div>
                      <label className="block text-xs font-medium text-[#6E6E6E] dark:text-gray-400 mb-1">Subscription Plan</label>
                      <p className="text-sm text-[#2C2C2C] dark:text-white font-medium">
                        {(() => {
                          try {
                            if (!companyProfile.businessSubscriptionPlan) return 'Not specified';
                            const plan = typeof companyProfile.businessSubscriptionPlan === 'string'
                              ? JSON.parse(companyProfile.businessSubscriptionPlan)
                              : companyProfile.businessSubscriptionPlan;
                            return plan.title || plan.planName || 'Not specified';
                          } catch {
                            return 'Not specified';
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-600 rounded-lg">
                  <label className="block text-xs font-medium text-[#6E6E6E] dark:text-gray-400 mb-2">Weekly Holidays</label>
                  {isEditingCompany ? (
                    <div className="grid grid-cols-2 gap-2">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                        <label key={day} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={companyFormData.weeklyHoliday?.includes(day) || false}
                            onChange={(e) => {
                              const currentHolidays = companyFormData.weeklyHoliday || [];
                              const updatedHolidays = e.target.checked
                                ? [...currentHolidays, day]
                                : currentHolidays.filter(h => h !== day);
                              handleCompanyFormChange('weeklyHoliday', updatedHolidays);
                            }}
                            className="w-4 h-4 text-[#7367F0] bg-gray-100 border-gray-300 rounded focus:ring-[#7367F0] dark:focus:ring-[#7367F0] dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <span className="text-sm text-[#2C2C2C] dark:text-white">{day}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    renderHolidayChips(companyProfile.weeklyHoliday)
                  )}
                </div>
              </div>


              {isEditingCompany && (
                <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end p-6 space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingCompany(false);
                      setCompanyFormData(companyProfile);  // Reset to original data
                      setUpdateCompanyError('');
                      setUpdateCompanySuccess('');
                    }}
                    className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                    disabled={updateCompanyLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-[#7367F0] text-white rounded-lg hover:bg-[#5A67D8] transition-colors font-medium flex items-center disabled:opacity-50 shadow-sm"
                    disabled={updateCompanyLoading}
                  >
                    {updateCompanyLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <FiCheckSquare className="mr-2" />
                        Update Company
                      </>
                    )}
                  </button>
                </div>
              )}


              {!isEditingCompany && (
                <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end p-6">
                  <button
                    onClick={() => {
                      setShowCompanyProfile(false);
                      setIsEditingCompany(false);
                      setCompanyFormData({});
                      setUpdateCompanyError('');
                      setUpdateCompanySuccess('');
                    }}
                    className="px-6 py-2.5 bg-[#7367F0] text-white rounded-lg hover:bg-[#5A67D8] transition-colors disabled:opacity-50 font-medium shadow-sm"
                    disabled={profileLoading}
                  >
                    Close
                  </button>
                </div>
              )}


            </form>
          )}

        </div>
      </div>
    );
  };


  // Render change password modal (keeping existing implementation)
  const renderChangePasswordModal = () => {
    if (!showChangePassword) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4 animate-fadeIn">
        <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full shadow-2xl animate-slideUp">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-[#2C2C2C] dark:text-white flex items-center">
              <FiLock className="mr-3 text-[#7367F0]" />
              Change Password
            </h2>
            <button
              onClick={() => {
                setShowChangePassword(false);
                setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setChangePasswordError('');
                setChangePasswordSuccess('');
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <FiX className="text-gray-500 dark:text-gray-400 text-xl" />
            </button>
          </div>

          <form onSubmit={handleChangePassword} className="p-6 space-y-4">
            {changePasswordError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 animate-shake flex items-start space-x-2">
                <FiAlertCircle className="text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-600 dark:text-red-400 text-sm font-medium">{changePasswordError}</p>
              </div>
            )}

            {changePasswordSuccess && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <p className="text-green-600 dark:text-green-400 text-sm flex items-center font-medium">
                  <FiCheckSquare className="mr-2" />
                  {changePasswordSuccess}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] dark:text-white mb-2">
                Current Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="password"
                  name="currentPassword"
                  value={changePasswordForm.currentPassword}
                  onChange={handleChangePasswordInputChange}
                  placeholder="Enter your current password"
                  className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#7367F0] focus:border-transparent transition-all bg-white dark:bg-gray-700 text-[#2C2C2C] dark:text-white"
                  required
                  disabled={changePasswordLoading}
                />
                <FiLock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] dark:text-white mb-2">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="password"
                  name="newPassword"
                  value={changePasswordForm.newPassword}
                  onChange={handleChangePasswordInputChange}
                  placeholder="Enter new password (min. 6 characters)"
                  className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#7367F0] focus:border-transparent transition-all bg-white dark:bg-gray-700 text-[#2C2C2C] dark:text-white"
                  required
                  minLength="6"
                  disabled={changePasswordLoading}
                />
                <FiLock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Must be at least 6 characters long</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] dark:text-white mb-2">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="password"
                  name="confirmPassword"
                  value={changePasswordForm.confirmPassword}
                  onChange={handleChangePasswordInputChange}
                  placeholder="Confirm your new password"
                  className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#7367F0] focus:border-transparent transition-all bg-white dark:bg-gray-700 text-[#2C2C2C] dark:text-white"
                  required
                  disabled={changePasswordLoading}
                />
                <FiCheckSquare className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowChangePassword(false);
                  setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setChangePasswordError('');
                  setChangePasswordSuccess('');
                }}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                disabled={changePasswordLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-[#7367F0] text-white rounded-lg hover:bg-[#5A67D8] transition-colors font-medium flex items-center justify-center disabled:opacity-50 shadow-sm"
                disabled={changePasswordLoading}
              >
                {changePasswordLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Changing...
                  </>
                ) : (
                  <>
                    <FiCheckSquare className="mr-2" />
                    Change Password
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };




  // Render notifications dropdown
  const renderNotificationsDropdown = () => {
    if (!isNotificationDropdownOpen) return null;

    const filteredNotifications = getFilteredNotifications();

    return (
      <div className="fixed inset-x-2 top-[70px] sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-white border border-gray-200 rounded-2xl shadow-xl z-[1000] sm:z-50 animate-slideDown notification-dropdown">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800 flex items-center">
              <FiBell className="mr-2 text-[#7367F0]" />
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {unreadCount}
                </span>
              )}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={markAllAsRead}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Mark all as read"
              >
                <FiCheckSquare className="text-gray-500 w-4 h-4" />
              </button>
              <button
                onClick={() => setIsNotificationDropdownOpen(false)}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="text-gray-500" />
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {['unread', 'all'].map((filter) => (
              <button
                key={filter}
                onClick={() => {
                  setNotificationFilter(filter);
                  if (filter === 'unread') {
                    setNotifications(prev => prev.map(n => n.justRead ? { ...n, justRead: false } : n));
                  }
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${notificationFilter === filter
                  ? 'bg-[#3A8DFF] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {filter === 'unread' ? 'Unread' : 'All'}
              </button>
            ))}
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {notificationLoading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#7367F0] border-t-transparent"></div>
              <span className="ml-2 text-sm text-[#6E6E6E] dark:text-gray-400">Loading...</span>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <FiBell className="mx-auto text-4xl text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredNotifications.map((notification, index) => (
                <div
                  key={index}
                  className={`p-4 hover:bg-gray-50 transition-colors group ${notification.unread ? 'bg-blue-50' : ''
                    }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${notification.unread ? 'bg-[#3A8DFF]' : 'bg-gray-300'
                      }`}></div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 cursor-pointer" onClick={() => handleNotificationClick(notification)}>
                          <p className="text-sm font-medium text-gray-800">
                            {notification.title}
                            {(notification.priority === 'high' || notification.priority === 'urgent') && (
                              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${notification.priority === 'urgent'
                                ? 'bg-red-100 text-red-600'
                                : 'bg-orange-100 text-orange-600'
                                }`}>
                                {notification.priority === 'urgent' ? '🔥 Urgent' : 'High Priority'}
                              </span>
                            )}
                            {notification.source === 'lead' && (
                              <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-600 text-xs rounded-full">
                                Lead
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{notification.message}</p>
                          {/* Lead-specific details */}
                          {notification.source === 'lead' && (notification.leadName || notification.leadPhone || notification.scheduledDate) && (
                            <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-500">
                              {notification.leadName && (
                                <span className="flex items-center gap-0.5">
                                  <FiUser className="w-3 h-3" /> {notification.leadName}
                                </span>
                              )}
                              {notification.leadPhone && (
                                <span className="flex items-center gap-0.5">
                                  <FiPhone className="w-3 h-3" /> {notification.leadPhone}
                                </span>
                              )}
                              {notification.scheduledDate && (
                                <span className="flex items-center gap-0.5">
                                  <FiCalendar className="w-3 h-3" /> {new Date(notification.scheduledDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                          )}
                          {notification.companyName && (
                            <p className="text-xs text-indigo-600 mt-0.5 font-medium">
                              {notification.companyName}
                            </p>
                          )}
                          <div className="flex items-center mt-2 text-xs text-gray-400">
                            <FiClock className="mr-1 w-3 h-3" />
                            {notification.time}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }}
                            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/40 rounded transition-colors"
                            title="Delete"
                          >
                            <FiX className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>


      </div>
    );
  };


  return (
    <div className="sticky top-0 z-[999] bg-gray-900 border-b border-gray-800 shadow-sm transition-colors">

      <style>{`

      
      
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(-10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
        .animate-slideDown { animation: slideDown 0.2s ease-out; }
        .animate-shake { animation: shake 0.3s ease-out; }
        
        .dark {
          color-scheme: dark;
        }
      `}</style>

      <div className="bg-gray-900 border-b border-gray-800 shadow-sm px-2 sm:px-4 md:px-6 h-[65px] flex flex-col justify-center transition-colors flex-shrink-0 text-white">
        <div className="flex items-center justify-between gap-1 md:gap-2">
          <div className="flex items-center gap-1 md:gap-4 min-w-0">
            {/* Hamburger menu — visible on mobile */}
            <button
              onClick={onToggleSidebar}
              className="md:hidden flex-shrink-0 p-1.5 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
              title="Toggle Sidebar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center space-x-2 min-w-0">
              <h1 className="text-[9px] sm:text-xs md:text-lg lg:text-2xl font-medium text-white whitespace-nowrap">{currentPage}</h1>
            </div>
            <div className="hidden lg:flex">
              {renderQuickStats()}
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-3 flex-shrink-0">
            {/* Create New Task Button */}
            {hasPermission('create_task') && (
              <button
                onClick={handleCreateTaskClick}
                className="flex group bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 hover:from-blue-700 hover:via-purple-700 hover:to-blue-800 text-white px-1.5 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg items-center shadow-md hover:shadow-xl transition-all duration-500 transform hover:-translate-y-0.5 hover:scale-105 relative overflow-hidden animate-pulse-slow"
                title="Create New Task"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10 flex items-center">
                  <div className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-2 bg-white/20 rounded-full flex items-center justify-center group-hover:rotate-180 transition-transform duration-500">
                    <FiPlus className="w-2.5 h-2.5 md:w-3 md:h-3" />
                  </div>
                  <span className="font-medium text-[10px] md:text-[13px] inline-block">
                    <span className="md:hidden">Task</span>
                    <span className="hidden md:inline">Create Task</span>
                  </span>
                </div>
              </button>
            )}

            {/* Create New lead Management Button */}
            {hasPermission('lead_management') && (
              <button
                onClick={handleCreateLeadClick}
                className="flex group bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 hover:from-blue-700 hover:via-purple-700 hover:to-blue-800 text-white px-1.5 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg items-center shadow-md hover:shadow-xl transition-all duration-500 transform hover:-translate-y-0.5 hover:scale-105 relative overflow-hidden"
                title="Generate & View Leads"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10 flex items-center">
                  <div className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-2 bg-white/20 rounded-full flex items-center justify-center group-hover:rotate-180 transition-transform duration-500">
                    <FiUsers className="w-2.5 h-2.5 md:w-3 md:h-3" />
                  </div>
                  <span className="font-medium text-[10px] md:text-[13px] inline-block">
                    <span className="md:hidden">Lead</span>
                    <span className="hidden md:inline">Add Leads</span>
                  </span>
                </div>
              </button>
            )}


            {/* Notifications */}
            <div className="relative notification-dropdown">
              <button
                onClick={toggleNotificationDropdown}
                className="relative p-1.5 md:p-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors"
                title="Notifications (Shift+N)"
              >
                <FiBell className="text-xl" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white font-medium">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {renderNotificationsDropdown()}
            </div>

            {/* Profile Dropdown */}
            <div className="relative profile-dropdown">
              <button
                onClick={toggleProfileDropdown}
                className="flex items-center p-1 px-1.5 md:p-1.5 md:px-2 hover:bg-gray-800 rounded-lg transition-all duration-200"
              >
                {renderUserProfile()}
              </button>

              {isProfileDropdownOpen && currentUser && (
                <div className="absolute right-0 top-full mt-2 w-72 sm:w-64 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 animate-slideDown">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-[#3A8DFF] to-[#1A73E8] rounded-full flex items-center justify-center shadow-md">
                        {currentUser.avatar || currentUser.profileImage || currentUser.businessLogo ? (
                          <img
                            src={currentUser.profileImage || currentUser.businessLogo}
                            alt={(currentUser.teamMemberName || currentUser.name || currentUser.businessName || 'User')}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-white font-bold">
                            {getUserInitials(currentUser.teamMemberName || currentUser.name || currentUser.businessName || 'User')}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {currentUser.teamMemberName || currentUser.name || currentUser.businessName || 'User'}
                        </p>
                        <p className="text-xs text-gray-500">{currentUser.role}</p>
                        <p className="text-xs text-gray-400">{currentUser.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="py-2">
                    {currentUser?.role === 'company' && currentUser?.id && (
                      <>
                        <button
                          onClick={() => {
                            fetchCompanyProfile();
                            setIsProfileDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3 disabled:opacity-50"
                          disabled={profileLoading || !currentUser?.id}
                        >
                          <FiUser className="text-[#3A8DFF]" />
                          <span className="text-sm text-gray-800">
                            {profileLoading ? 'Loading...' : 'View Profile'}
                          </span>
                        </button>

                      </>
                    )}

                    <button
                      onClick={() => {
                        setShowChangePassword(true);
                        setIsProfileDropdownOpen(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                    >
                      <FiLock className="text-[#3A8DFF]" />
                      <span className="text-sm text-gray-800">Change Password</span>
                    </button>
                  </div>

                  <div className="border-t border-gray-100"></div>

                  <div className="py-2">
                    <button
                      onClick={onLogout}
                      className="w-full px-4 py-3 text-left hover:bg-red-50 transition-colors flex items-center space-x-3 text-red-600"
                    >
                      <FiLogOut />
                      <span className="text-sm font-medium">Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>




      {/* Modals */}

      {renderCompanyProfileModal()}
      {renderChangePasswordModal()}


      {/* Task Form Modal */}
      {showTaskForm && (
        <TaskForm
          key={taskFormData ? `edit-${taskFormData.id}` : 'create-new'}
          initialData={taskFormData}
          onSubmit={handleTaskFormSubmit}
          onClose={handleTaskFormClose}
          userDepartments={departments}
          userRole={userRole || 'admin'}
        />
      )}

      {/* Lead Form Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 z-[9999]">
          <div className="bg-[#fff] rounded-none sm:rounded-xl shadow-2xl max-w-2xl w-full h-full sm:h-auto max-h-full sm:max-h-[90vh] flex flex-col overflow-hidden animate-slideUp">
            <LeadForm
              lead={null}
              onSubmit={handleAddLeadSubmit}
              onCancel={() => setShowLeadForm(false)}
            />
          </div>
        </div>
      )}

    </div>
  );
}

export default TopBar;
